import { theme as antdTheme, type ThemeConfig } from 'antd';
import { radius, semantic, surface, typography } from './tokens';

/**
 * Produce the AntD v5 `ThemeConfig` from our brand tokens.
 *
 * Garden Art brand is now LIVE (post-S3 polish). Every color, font, and
 * surface tone is sourced from `tokens.ts` so future theme changes flow
 * through one place. The token comment in tokens.ts has the rationale
 * for each value.
 */
export function buildAntdTheme(opts: { isArabic: boolean }): ThemeConfig {
  return {
    algorithm: antdTheme.defaultAlgorithm,
    token: {
      // ---- Brand --------------------------------------------------------
      colorPrimary: semantic.brand.primary,
      colorPrimaryHover: semantic.brand.primaryHover,
      colorPrimaryActive: semantic.brand.primaryActive,
      colorLink: semantic.brand.primary,
      colorLinkHover: semantic.brand.primaryHover,
      // ---- Status -------------------------------------------------------
      colorSuccess: semantic.status.success,
      colorWarning: semantic.status.warning,
      colorError: semantic.status.error,
      colorInfo: semantic.status.info,
      // ---- Shape --------------------------------------------------------
      borderRadius: radius.lg, // Slightly rounder (8) than default (6) for premium feel
      borderRadiusSM: radius.sm,
      borderRadiusLG: radius.xl,
      // ---- Typography ---------------------------------------------------
      fontFamily: opts.isArabic ? typography.fontFamilyRtl : typography.fontFamilyLtr,
      fontSize: typography.size.base,
      fontWeightStrong: 600,
      // ---- Surfaces -----------------------------------------------------
      colorBgLayout: surface.page,
      colorBgContainer: surface.card,
      colorBorder: surface.border,
      colorBorderSecondary: surface.borderSubtle,
      colorText: surface.textPrimary,
      colorTextSecondary: surface.textSecondary,
      colorTextTertiary: surface.textTertiary,
      // ---- Motion -------------------------------------------------------
      motionDurationMid: '200ms',
      motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    components: {
      Layout: {
        // Header + Sider use brand-tinted dark surfaces instead of AntD navy.
        headerBg: surface.headerDark,
        siderBg: surface.railDark,
        bodyBg: surface.page,
      },
      Menu: {
        // Dark menus (primary rail) read against the evergreen rail.
        darkItemBg: surface.railDark,
        darkSubMenuItemBg: surface.railDark,
        darkItemSelectedBg: semantic.brand.primaryHover,
        darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
        darkItemSelectedColor: '#FFFFFF',
        // Light module sidebar — match the warm surface palette.
        itemSelectedBg: 'rgba(31, 77, 58, 0.08)',
        itemSelectedColor: semantic.brand.primary,
        itemHoverBg: 'rgba(31, 77, 58, 0.04)',
      },
      Card: {
        borderRadiusLG: radius.xl,
        boxShadowTertiary: '0 1px 2px rgba(15, 42, 32, 0.04)',
      },
      Button: {
        borderRadius: radius.lg,
        controlHeight: 36,
      },
      Tag: {
        borderRadiusSM: radius.sm,
      },
      Table: {
        headerBg: surface.muted,
        headerColor: surface.textSecondary,
        rowHoverBg: 'rgba(31, 77, 58, 0.03)',
      },
    },
  };
}
